import { useSelector, useDispatch } from "react-redux";
import { FormattedMessage, FormattedNumber } from "react-intl";
import PropTypes from "prop-types";
import { CheckCircleOutline as CheckCircleOutlineIcon } from "@material-ui/icons";
import { Box, Chip, Grid } from "@material-ui/core";
import { SrTable, Typography } from "components/common";
import ExpressCharge from "assets/images/ExpressCharge/ExpressCharge.svg";
import useStyles from "./expressChargeStyles";
import translate from "../../../../../i18n/translate";
import { clearSuccess } from "../../../../../redux/app/actions/ecommerce";
import { CURRENCY } from "../../../../../constants";
import {
  SrButton,
  SrDialog,
  SrContentHeader,
  SrContentWrapper,
  ActionsTable,
} from "../../../../common";

const GetCardPayments = ({ onApply, loading, cardPayments, isLoading }) => {
  const {
    ecommerce: { dialogs },
  } = useSelector(({ ecommerce }) => ecommerce);

  const classes = useStyles();
  const dispatch = useDispatch();

  const getStatusChip = (status, id) => {
    const chips = [
      <Chip key={id} label={translate("common.expired")} color="secondary" />,
      <Chip key={id} label={translate("common.pending")} color="default" />,
      <Chip key={id} label={translate("common.paid")} color="primary" />,
    ];

    return chips[status];
  };

  const amountFormat = (amount) => (
    <FormattedNumber value={amount} style="currency" currency={CURRENCY.MXN} />
  );

  const columns = [
    {
      header: translate("common.product"),
      id: "product",
      isSortable: true,
      minWidth: 150,
    },
    {
      header: translate("common.status"),
      id: "status",
      cell: ({ status, id }) => getStatusChip(status, id),
      align: "center",
      minWidth: 100,
    },
    {
      header: translate("common.amount"),
      id: "amount",
      cell: ({ amount }) => amountFormat(amount),
      align: "center",
      isSortable: true,
      minWidth: 100,
    },
    {
      header: translate("common.actions"),
      id: "actions",
      cell: ({ url, status }) => (
        <>
          {status !== 0 && (
            <ActionsTable url={url} type={status === 1 ? "order" : "voucher"} />
          )}
        </>
      ),
      align: "center",
      isSortable: false,
      minWidth: 100,
    },
  ];

  const handleClose = (type) => {
    if (type === "success") {
      dispatch(clearSuccess());
    }
  };

  return (
    <Grid container flexDirection="column" spacing={2}>
      <SrContentHeader
        title={translate("ecommerce.cobroExpress.getCardPayments.heading")}
      />
      <SrContentWrapper isLoading={isLoading}>
        <Box marginBottom={6} marginTop={2} maxWidth="536px">
          <Box
            display="flex"
            flexDirection="column"
            alignItems="center"
            paddingY={8}
            paddingX={6}
            bgcolor="#22D3B9"
            color="white"
            className={classes.borderBox}
          >
            <img src={ExpressCharge} alt={translate("common.requestPayment")} />
            <Box marginY={3.75}>
              <Typography variant="h3SemiBold">
                <FormattedMessage
                  id="ecommerce.cobroExpress.cardHeading"
                  defaultMessage="¿Quieres crear una liga de cobro?"
                />
              </Typography>
            </Box>
            <SrButton color="primary" variant="contained" onClick={onApply}>
              <FormattedMessage id="common.apply" defaultMessage="Solicitar" />
            </SrButton>
          </Box>
        </Box>
        <SrTable rows={cardPayments} columns={columns} loading={loading} />
        {dialogs?.success?.cobroPaymentRequest?.shown && (
          <SrDialog
            handleClose={() => {
              handleClose("success");
            }}
            open={dialogs?.success?.cobroPaymentRequest?.shown}
            heading={dialogs?.success?.cobroPaymentRequest?.title}
          >
            <div className={classes.dialogContent}>
              <CheckCircleOutlineIcon
                color="primary"
                fontSize="large"
                className={classes.checkcircle}
              />
              <Typography>
                {dialogs?.success?.cobroPaymentRequest?.message}
              </Typography>
              <Box display="flex" justifyContent="center" mt={4}>
                <SrButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  onClick={() => {
                    handleClose("success");
                  }}
                >
                  {translate("common.close")}
                </SrButton>
              </Box>
            </div>
          </SrDialog>
        )}
      </SrContentWrapper>
    </Grid>
  );
};

GetCardPayments.propTypes = {
  onApply: PropTypes.func.isRequired,
  isLoading: PropTypes.bool.isRequired,
};

export default GetCardPayments;
